import { create } from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';

interface PreferencesStore {
  language: 'en' | 'zh-HK';
  notificationsEnabled: boolean;
  dataSharing: {
    analytics: boolean;
    marketing: boolean;
    research: boolean;
  };
  setLanguage: (language: 'en' | 'zh-HK') => void;
  setNotificationsEnabled: (enabled: boolean) => void;
  updateDataSharing: (key: keyof PreferencesStore['dataSharing'], value: boolean) => void;
  loadPreferences: () => Promise<void>;
  savePreferences: () => Promise<void>;
}

const STORAGE_KEY = '@aia_preferences';

export const usePreferencesStore = create<PreferencesStore>((set, get) => ({
  language: 'en',
  notificationsEnabled: true,
  dataSharing: {
    analytics: false,
    marketing: false,
    research: false,
  },

  setLanguage: async (language: 'en' | 'zh-HK') => {
    set({ language });
    await get().savePreferences();
  },

  setNotificationsEnabled: async (enabled: boolean) => {
    set({ notificationsEnabled: enabled });
    await get().savePreferences();
  },

  updateDataSharing: async (key, value) => {
    set(state => ({
      dataSharing: {
        ...state.dataSharing,
        [key]: value,
      },
    }));
    await get().savePreferences();
  },

  loadPreferences: async () => {
    try {
      const stored = await AsyncStorage.getItem(STORAGE_KEY);
      if (stored) {
        const preferences = JSON.parse(stored);
        set(preferences);
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }
  },

  savePreferences: async () => {
    try {
      const { language, notificationsEnabled, dataSharing } = get();
      await AsyncStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ language, notificationsEnabled, dataSharing })
      );
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  },
}));
